import React, {
  createRef,
  MutableRefObject,
  PropsWithChildren,
  ReactElement,
  useCallback,
} from 'react'
import { Flex, Text, ThemeUIStyleObject } from 'theme-ui'
import useDropdownKeyboardControl from 'utils/useDropdownKeyboardControl'
import { useAppSelector } from 'store/hooks'
import { SimpleActionMenuItem, SimpleActionMenuItemProps } from './SimpleActionMenuItem'

export const actionMenuScrollingContainerRef = createRef<HTMLDivElement>()

export interface SimpleActionMenuProps {
  dataTestId?: string
  items: SimpleActionMenuItemProps[]
  sxContainer?: ThemeUIStyleObject
  sxButton?: ThemeUIStyleObject
  customHoverIndex?: number
  customHoverStyles?: ThemeUIStyleObject
  hasTopBorder?: boolean
  highlightedBackgroundColor?: string
  hideRef?: MutableRefObject<(() => void) | undefined>
  isOpen?: boolean
  shouldHighlightOnHover?: boolean
  tabIndex?: number
  className?: string
  groupBy?: { key: string; label: string }[]
}
export default function SimpleActionMenu({
  dataTestId,
  items,
  sxContainer,
  sxButton,
  customHoverIndex,
  customHoverStyles,
  hasTopBorder,
  hideRef,
  isOpen,
  highlightedBackgroundColor = 'white6',
  shouldHighlightOnHover = true,
  tabIndex,
  className,
  groupBy,
}: SimpleActionMenuProps): ReactElement {
  const isSearchDropdownVisible = useAppSelector(s => s.dashboard.isSearchDropdownVisible)
  const hoverIndex = useDropdownKeyboardControl(
    items.length,
    isSearchDropdownVisible,
    hideRef,
    isOpen,
  )
  const keyboardHoverIndex = isSearchDropdownVisible ? customHoverIndex : hoverIndex

  const getActionMenuItems = useCallback(
    (menuItems?: SimpleActionMenuItemProps[]) =>
      menuItems?.map(({ sx, ...item }, index) => {
        return (
          <SimpleActionMenuItem
            key={index}
            sx={{ ...sxButton, ...sx }}
            {...item}
            isKeyboardHover={keyboardHoverIndex === index}
            customHoverStyles={customHoverStyles}
            isBackgroundHighlighted
            highlightedBackgroundColor={
              item.highlightedBackgroundColor || highlightedBackgroundColor
            }
            shouldHighlightOnHover={shouldHighlightOnHover}
            tabIndex={tabIndex}
          />
        )
      }) || [],
    [
      customHoverStyles,
      highlightedBackgroundColor,
      keyboardHoverIndex,
      shouldHighlightOnHover,
      sxButton,
      tabIndex,
    ],
  )

  return (
    <Flex
      ref={actionMenuScrollingContainerRef}
      data-testid={dataTestId || 'dropdown-menu'}
      role="menu"
      id="dropdown-menu"
      aria-activedescendant={keyboardHoverIndex !== -1 ? `option-${keyboardHoverIndex}` : undefined}
      sx={{
        minWidth: ['60vw', 'unset'],
        flexDirection: 'column',
        px: 0,
        pt: 0,
        py: hasTopBorder ? '0.4rem' : '0.4rem',
        gap: '0.1rem',
        borderTop: hasTopBorder ? '1px solid' : 'none',
        borderColor: hasTopBorder ? 'white15' : 'none',
        lineHeight: hasTopBorder ? '2.2rem' : 'none',
        overscrollBehavior: 'contain',
        ...sxContainer,
      }}
      className={className || 'show-scrollbar'}
    >
      {groupBy?.length ? (
        <>
          {/* selected items */}
          {getActionMenuItems(items.filter(item => item.isSelected))}

          {/* grouped items */}
          {groupBy.map(group => {
            const groupItems = items.filter(
              item => item.groupByKey === group.key && !item.isSelected,
            )

            return (
              <GroupByWrapper
                key={group.key}
                groupLabel={group.label}
                isLabelVisible={!!groupItems.length}
              >
                {getActionMenuItems(groupItems)}
              </GroupByWrapper>
            )
          })}
        </>
      ) : (
        // items without grouping
        getActionMenuItems(items)
      )}
    </Flex>
  )
}

function GroupByWrapper({
  children,
  groupLabel,
  isLabelVisible = true,
}: PropsWithChildren<{ groupLabel: string; isLabelVisible?: boolean }>): ReactElement {
  return (
    <>
      {isLabelVisible && (
        <Text
          data-testid={`${groupLabel}-group-label`}
          variant="size12Weight700Uppercase"
          sx={{
            color: 'white50',
            my: '0.8rem',
          }}
        >
          {groupLabel}
        </Text>
      )}
      {children}
    </>
  )
}
